import { VStack } from '@genial/components';
import { Spacing } from '@genial/design-tokens';
import { ErrorListResponse, instanceOfErrorListResponse, Service } from '@genial/services';
import {
  InteractionChannelAuditRequest,
  ResponsesCoeOrderBuyResponse,
} from '@genial/services/dist/models';
import React from 'react';

import { useSendingChannels } from '../../../../../../../../hooks/useSendingChannels';
import { useToastCreator } from '../../../../../../../../hooks/useToastCreator';
import { useCoesAssetController } from '../../../../../../../BatchTicket/components/DynamicAsset/hooks/products/Coes';
import {
  orderResumePdfTemplate,
  parsePDFDataExport,
  setupPDF,
} from '../../../../../../../Resume/utils';
import { CardHeader } from '../../components/CardHeader';
import { ConfirmationButton } from '../../components/ConfirmationButton';
import { SimplifiedApplicationMissingTerm } from '../../components/SimplifiedApplicationMissingTerm';
import { TicketApplicationInProgress } from '../../components/TicketApplicationInProgress';
import { TicketApplicationSuccessDetails } from '../../components/TicketApplicationSuccessDetails';
import { defaultTicketApplicationBaseInfo } from '../../components/TicketApplicationSuccessDetails/constants';
import { TicketDetails } from '../../components/TicketDetails';
import {
  defaultErrorMessageApplication,
  screenName,
  screenNameSimplifiedFlow,
} from '../../constants';
import { CustomerApplicationModalProductProps } from '../../types';

import { CoesInvestCard } from './components/CoesInvestCard';
import { getCoesSuccessInfos } from './constants';

export interface CoesApplicationProps extends CustomerApplicationModalProductProps<'coes'> {
  onClose: () => void;
  handleFollowTicket: () => void;
}

export const CoesApplication: React.FC<CoesApplicationProps> = ({
  offer,
  onClose,
  customerId,
  productType,
  accountNumber,
  handleFollowTicket,
  isSimplifiedApplication,
  isSimplifiedApplicationFlagMatched,
  showSendingChannels,
  clientHasTerm,
  isClientTermLoading,
}) => {
  const { showToastFromError, showToast } = useToastCreator('coesApplicationInCustomer');
  const [sendingOrder, setSendingOrder] = React.useState(false);
  const [interactionChannelAudit, setInteractionChannelAudit] =
    React.useState<InteractionChannelAuditRequest | null>();
  const [billingTypeSelected, setBillingTypeSelected] = React.useState<string>();
  const [success, setSuccess] = React.useState(false);
  const [successInfos, setSuccessInfos] = React.useState(defaultTicketApplicationBaseInfo);

  const [ticketSuccessData, setTicketSuccessData] = React.useState<ResponsesCoeOrderBuyResponse>();

  const offerController = useCoesAssetController(offer);
  const sendingChannelsController = useSendingChannels(customerId);

  const buttonDisabled =
    !!offerController.hasError ||
    (!sendingChannelsController.sendingChannelsSelected.length && !isSimplifiedApplication) ||
    !billingTypeSelected ||
    interactionChannelAudit === undefined;

  const handleExportNewOrder = () => {
    if (!ticketSuccessData) {
      return;
    }
    const data = parsePDFDataExport(ticketSuccessData as any);
    setupPDF(
      orderResumePdfTemplate(data, 'StructuredOperations'),
      `ordem-${successInfos.orderHash}.pdf`
    );
  };

  const handleConfirmOrder = () => {
    setSendingOrder(true);
    Service.COES.V1.postOrdersBuy({
      screenName: isSimplifiedApplication ? screenNameSimplifiedFlow : screenName,
      coeOrderBuyRequest: {
        accountNumber,
        productId: offer.productIdNormalized,
        orderAmount: offerController.currentValue,
        billingTypeId: parseInt(billingTypeSelected || '', 10),
        notificationChannels: isSimplifiedApplication
          ? null
          : sendingChannelsController.sendingChannelsSelected,
        orderName: 'Operações Estruturadas',
        interactionChannelAudit: interactionChannelAudit!,
      },
    })
      .then(({ data }) => {
        setSuccess(true);
        setTicketSuccessData(data);
        setSuccessInfos(
          getCoesSuccessInfos(
            data.offer,
            data.order,
            showSendingChannels,
            isSimplifiedApplicationFlagMatched
          )
        );
      })
      .catch((error) => {
        if (instanceOfErrorListResponse(error as object)) {
          (error as ErrorListResponse).errors?.forEach((errorItem) =>
            showToastFromError(errorItem)
          );
          return;
        }

        showToast(...defaultErrorMessageApplication);
      })
      .finally(() => setSendingOrder(false));
  };

  if (success) {
    return (
      <TicketApplicationSuccessDetails
        {...successInfos}
        onClose={onClose}
        onFollow={handleFollowTicket}
        onExportPdf={handleExportNewOrder}
      />
    );
  }

  if (sendingOrder) {
    return <TicketApplicationInProgress />;
  }

  return (
    <VStack space={Spacing['stack.xxxs']}>
      <CardHeader onClose={onClose} />

      <CoesInvestCard
        offer={offer}
        offerController={offerController}
        billingTypeSelected={billingTypeSelected}
        setBillingTypeSelected={setBillingTypeSelected}
      />

      <SimplifiedApplicationMissingTerm
        isClientTermAvailable={clientHasTerm}
        isClientTermLoading={isClientTermLoading}
        isSimplifiedApplicationFlagMatched={isSimplifiedApplicationFlagMatched}
      />

      <TicketDetails
        productTypeId={productType}
        sendingChannelsController={sendingChannelsController}
        setInteractionChannelAudit={setInteractionChannelAudit}
        interactionChannelAudit={interactionChannelAudit}
        showSendingChannels={showSendingChannels}
        isSimplifiedApplicationFlagMatched={isSimplifiedApplicationFlagMatched}
      />

      <ConfirmationButton onConfirm={handleConfirmOrder} disabled={buttonDisabled} />
    </VStack>
  );
};
